import { __rest } from '../../../../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useId, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import cns from '../../../../node_modules/classnames/index.js';
import useChangeModalPosition from '../../../Modal/useChangeModalPosition.js';
import usePortalElement from '../../../Modal/usePortalElement.js';
import '../../../../utils/log.js';
import Header from './Header.js';
import Body from './Body.js';
import Footer from './Footer.js';
import GenericItem from './GenericItem.js';
import ConfirmItem from './ConfirmItem.js';
import FeedbackItem from './FeedbackItem.js';
import useDontShowAgainMessageRead from '../../../DontShowAgain/useDontShowAgainMessageRead.js';
import DontShowAgain from '../../../DontShowAgain/DontShowAgain.js';
import { isClosedByDontShowAgainEvent, ONLY_ONE_CHILD, createClosedByDontShowAgainEvent } from './utils.js';
import { EmphasisLM } from '../../../types.js';
import useModalContext from '../../../ModalContext/useModalContext.js';
import ModalProvider from './ModalProvider.js';
import useModalTransaction from './useModalTransaction.js';

/**
Visualizza un contenuto che blocca temporaneamente le interazioni con il resto dell'interfaccia.

Vedi [Design System di CGN - Modale](https://zeroheight.com/1b5e94371/p/992bf6-modale/b/9260db)

Si può utilizzare per questi tipi di messaggi:

- Chiedere conferma o fare decisioni
- Fornire delle informazioni
- Completare dei compiti importanti all'interno di un processo

La modale è creata a partire da un elemento figlio del body della pagina.

_Nota_: Se la modale non contiene delle azioni (ad es. Salva),
ma contiene ad es. un processo che richiede i pulsanti "Indietro" e "Avanti",
bisogna inserire il pulsante "X" per poterla chiudere.

_Esempio struttura del componente_

```js
<Modal ... >
  <Modal.Header>
    ...
  </Modal.Header>

  <Modal.Body>
    ...
  </Modal.Body>

  <Modal.Footer>
    ...
  </Modal.Footer>
</Modal>
```

E' inoltre possibile definire una serie di contenuti di una modale che verranno presentati come un gruppo unico.

Ogni componente manterrà il suo stato e verrà distrutto solo alla chiusura della modale.

La proprietà `current` definisce l'elemento presentato.

_Esempio struttura del componente_

```js
<Modal current="modale1" ... >
  <Modal.Generic id="modale1" >
    ...
  </Modal.Generic>

  <Modal.Confirm
    id="modale2"
    ...
  />

  <Modal.Feedback
    id="modale3"
    ...
  />
</Modal>
```

  */
function Modal(_a) {
    var { show = false, doNotFocus = false, className = undefined, contentClassName = '', rootElementClassName, children, dontShowAgainId = undefined, dontShowAgainMessage = 'Non mostrare più questo messaggio', onClose = undefined, current = ONLY_ONE_CHILD, transitionDuration = 500, initialPos = {
        x: 'auto',
        y: (window.innerHeight * 0.05),
    }, diffModalContentModalBody = 260, style = {}, portalElementRef, emphasis = EmphasisLM.MEDIUM } = _a, others = __rest(_a, ["show", "doNotFocus", "className", "contentClassName", "rootElementClassName", "children", "dontShowAgainId", "dontShowAgainMessage", "onClose", "current", "transitionDuration", "initialPos", "diffModalContentModalBody", "style", "portalElementRef", "emphasis"]);
    const contentRef = useRef(null);
    useModalContext(show);
    const { x, y, overflow, maxHeight, dragging, onMouseDown, } = useChangeModalPosition(initialPos, contentRef, portalElementRef, diffModalContentModalBody);
    const messageRead = useDontShowAgainMessageRead(dontShowAgainId, show);
    const { state: transactionRunning, current: transactionCurrent } = useModalTransaction(current, transitionDuration);
    useEffect(() => {
        if (show && !doNotFocus && contentRef.current)
            contentRef.current.focus();
    }, [doNotFocus, show]);
    useEffect(() => {
        if (portalElementRef.current && rootElementClassName) {
            portalElementRef.current.className = '';
            portalElementRef.current.classList.add(rootElementClassName);
        }
    }, [portalElementRef, rootElementClassName]);
    if (!show)
        return null;
    if (messageRead) {
        onClose === null || onClose === void 0 ? void 0 : onClose(createClosedByDontShowAgainEvent(undefined, dontShowAgainId));
        return null;
    }
    let child = children;
    if (onClose || dontShowAgainId) {
        // modale singola
        child = (jsx(GenericItem, { id: current, onClose: onClose, dontShowAgainId: dontShowAgainId, dontShowAgainMessage: dontShowAgainMessage, children: children }));
    }
    return (jsxs("div", { role: "dialog", className: cns('v2-modal', `v2-modal--${emphasis.toLowerCase()}`, className), children: [jsx("div", { className: "v2-modal__overlay" }), jsx("div", Object.assign({ style: Object.assign({ marginLeft: x, marginTop: y, transition: `opacity ${transitionDuration}ms` }, style), tabIndex: -1, ref: contentRef, className: cns('v2-modal__content', { 'v2-modal__content--changing': transactionRunning }, contentClassName) }, others, { children: jsx(ModalProvider, { current: transactionCurrent, overflow: overflow, maxHeight: maxHeight, dragging: dragging, onMouseDown: onMouseDown, children: child }) }))] }));
}
const ModalPortal = function ModalPortal(props) {
    // uso questo wrapper perchè altrimenti storybook non estrae le props se si usa createPortal!!!
    const modalId = useId();
    const portalElementRef = usePortalElement(false, modalId);
    return portalElementRef.current ? createPortal(jsx(Modal, Object.assign({}, props, { portalElementRef: portalElementRef })), portalElementRef.current) : null;
};
ModalPortal.Header = Header;
ModalPortal.Body = Body;
ModalPortal.Footer = Footer;
ModalPortal.Generic = GenericItem;
ModalPortal.Confirm = ConfirmItem;
ModalPortal.Feedback = FeedbackItem;
ModalPortal.clearMessageStatus = DontShowAgain.clearMessageStatus;
ModalPortal.isDontShowAgainOptionSelected = DontShowAgain.isDontShowAgainOptionSelected;
ModalPortal.isClosedByDontShowAgainEvent = isClosedByDontShowAgainEvent;

export { Modal, ModalPortal as default };
